import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Skeleton } from '@/shared/ui/atoms';

import type { TutorUsageTimelinePoint } from '../../../types/tutorAi';
import { formatAxisDate, formatCompactCount, formatCount, formatDate } from '../../../utils/format';
import { ChartTooltip } from '../../atoms/ChartTooltip';
import { SurfaceCard } from '../../molecules/SurfaceCard';

/**
 * `syncId` compartilhado pelos dois painéis: é o que faz o Recharts espelhar o hover de um
 * no outro. Sem ele a leitura vertical — mesma data, mesma coluna — não existiria, e essa
 * leitura é a razão de a tela não usar um eixo Y duplo.
 */
const SYNC_ID = 'tutor-usage-evolution';

/** Altura do painel de tokens, o principal. Espelhada no placeholder de carregamento. */
const TOKENS_PANEL_HEIGHT = 'h-[11rem]';

/** Altura do painel de mensagens, o de apoio. Espelhada no placeholder de carregamento. */
const MESSAGES_PANEL_HEIGHT = 'h-[8rem]';

/**
 * Altura do skeleton que substitui o subtítulo de painel (`ts-caption-xxs`) durante o
 * carregamento — a mesma usada em qualquer placeholder desse estilo de texto na feature
 * (ver `KpiCardSkeleton`), para o card não crescer quando os dados chegam.
 */
const PANEL_HEADING_SKELETON_HEIGHT = 'h-mini';

/** Largura reservada ao eixo Y. Igual nos dois painéis, senão as colunas desalinham. */
const Y_AXIS_WIDTH = 56;

/** Margem interna dos dois plots, para o traço não encostar na borda do card. */
const PLOT_MARGIN = { top: 8, right: 8, bottom: 0, left: 8 } as const;

/**
 * Props do grid pontilhado de fundo, idênticas nos dois painéis. Extraída como objeto — não
 * como componente — porque o Recharts inspeciona o *tipo* dos filhos diretos de `AreaChart`/
 * `BarChart`: `<CartesianGrid {...GRID_PROPS} />` continua sendo um `CartesianGrid` de
 * verdade, então o Recharts o reconhece; um wrapper próprio não seria reconhecido.
 */
const GRID_PROPS = {
  className: 'text-ctx-layout-border',
  stroke: 'currentColor',
  strokeDasharray: '3 3',
  vertical: false,
} as const;

/** Formata o tick do eixo Y em notação compacta — mesmo formatador nos dois painéis. */
function formatCompactAxisTick(value: number | string): string {
  return formatCompactCount(Number(value));
}

/**
 * Props do eixo Y, idênticas nos dois painéis (mesma largura, formatação e ausência de
 * linha), pela mesma razão de `GRID_PROPS`: `<YAxis {...Y_AXIS_PROPS} />` permanece um
 * `YAxis` de verdade para o Recharts.
 */
const Y_AXIS_PROPS = {
  className: 'text-ctx-content-placeholder',
  stroke: 'currentColor',
  tick: { fill: 'currentColor', fontSize: 11 },
  tickLine: false,
  axisLine: false,
  width: Y_AXIS_WIDTH,
  tickFormatter: formatCompactAxisTick,
} as const;

/** Props do `UsageEvolutionChart`. */
export interface UsageEvolutionChartProps {
  /** Série diária de tokens e mensagens do período filtrado, já normalizada pelo hook. */
  readonly timeline: readonly TutorUsageTimelinePoint[];
  /** Exibe placeholders da mesma altura dos painéis enquanto o período é consultado. */
  readonly isLoading: boolean;
}

/** Subtítulo que nomeia a medida de um painel. */
function PanelHeading({ children }: { readonly children: string }) {
  return <span className="px-xxs text-ctx-content-placeholder ts-caption-xxs">{children}</span>;
}

/**
 * Placeholder do subtítulo de painel durante o carregamento — mesmo espaçamento horizontal
 * de `PanelHeading` (`px-xxs`), só troca o texto por um bloco animado.
 */
function PanelHeadingSkeleton() {
  return (
    <div className="px-xxs">
      <Skeleton className={`w-[9rem] ${PANEL_HEADING_SKELETON_HEIGHT}`} />
    </div>
  );
}

/**
 * Conteúdo do tooltip dos dois painéis: só o rótulo da série muda, o formatador de valor e
 * de data (eixo X) são os mesmos.
 */
function renderChartTooltip(seriesLabel: string): ReactElement {
  return (
    <ChartTooltip seriesLabel={seriesLabel} formatValue={formatCount} formatLabel={formatDate} />
  );
}

/**
 * Evolução diária de tokens e de mensagens do período filtrado.
 *
 * São dois painéis empilhados dividindo o eixo X, não um gráfico de eixo Y duplo. O eixo
 * duplo alinharia duas escalas arbitrariamente e inventaria uma correlação: como tokens são
 * derivados de mensagens, as duas curvas sairiam quase idênticas todos os dias, escondendo
 * justamente o sinal interessante — quando as conversas ficaram mais caras por mensagem.
 * Empilhados, cada série mantém sua escala real e a correlação se lê na vertical.
 *
 * As formas diferem (área para o custo contínuo, barra para a contagem discreta) e cada
 * painel é titulado, então a identidade da série nunca depende só da cor — e nenhum dos dois
 * precisa de legenda, porque cada um tem uma série só.
 *
 * A cor vem de `currentColor`: o container define `text-base-accent-500` e os elementos do
 * SVG herdam, o que mantém a regra de não referenciar `@ads/tokens` no componente e preserva
 * a troca de tema (o passo 500 passa contraste nos dois temas).
 */
export function UsageEvolutionChart({ timeline, isLoading }: UsageEvolutionChartProps) {
  const { t } = useTranslation('tutorAI');

  // Vazio só quando as duas séries estão zeradas: um período com tokens e sem mensagens
  // (ou o inverso) renderiza os dois painéis, e a linha de base em zero é informação.
  const hasData = timeline.some(point => point.tokensUsed > 0 || point.messages > 0);

  // O Recharts tipa `data` como mutável e a timeline é readonly por contrato do hook.
  // Cópia em vez de type assertion: custa uma alocação por render (um item por dia do
  // período) e evita mentir para o compilador sobre a mutabilidade.
  const data = [...timeline];

  if (isLoading) {
    // Mesmo container (`gap-nano py-xxs`) e os mesmos quatro filhos do estado carregado
    // (subtítulo + painel, duas vezes) — senão o card cresce quando os dados chegam.
    return (
      <SurfaceCard title={t('usage.charts.title')}>
        <div className="flex flex-col gap-nano py-xxs">
          <PanelHeadingSkeleton />
          <Skeleton className={`w-full ${TOKENS_PANEL_HEIGHT}`} />
          <PanelHeadingSkeleton />
          <Skeleton className={`w-full ${MESSAGES_PANEL_HEIGHT}`} />
        </div>
      </SurfaceCard>
    );
  }

  if (!hasData) {
    return (
      <SurfaceCard title={t('usage.charts.title')}>
        <p className="py-xs text-center text-ctx-content-placeholder ts-paragraph-xs">
          {t('usage.charts.empty')}
        </p>
      </SurfaceCard>
    );
  }

  return (
    <SurfaceCard title={t('usage.charts.title')}>
      <div className="flex flex-col gap-nano py-xxs text-base-accent-500">
        <PanelHeading>{t('usage.charts.tokensPanel')}</PanelHeading>
        <div className={`w-full ${TOKENS_PANEL_HEIGHT}`}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} syncId={SYNC_ID} margin={PLOT_MARGIN}>
              <CartesianGrid {...GRID_PROPS} />
              {/* `dataKey` fica mesmo escondido: é ele que casa a categoria com o outro painel.
                  `scale="band"` é obrigatório aqui: sem ele o Recharts resolve `auto` como
                  escala `point` num AreaChart (mas `band` num BarChart — ver combineRealScaleType),
                  e bandwidth() de uma escala point é 0. O ponto da área cairia então na borda de
                  cada categoria enquanto a barra do outro painel fica no centro, desalinhando os
                  dois painéis (pior nas duas pontas) apesar do `syncId` sincronizar os tooltips
                  corretamente por índice. */}
              <XAxis dataKey="date" hide scale="band" />
              <YAxis {...Y_AXIS_PROPS} />
              <Tooltip content={renderChartTooltip(t('usage.charts.tokensSeriesLabel'))} />
              <Area
                type="monotone"
                dataKey="tokensUsed"
                stroke="currentColor"
                strokeWidth={2}
                fill="currentColor"
                fillOpacity={0.12}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <PanelHeading>{t('usage.charts.messagesPanel')}</PanelHeading>
        <div className={`w-full ${MESSAGES_PANEL_HEIGHT}`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} syncId={SYNC_ID} margin={PLOT_MARGIN}>
              <CartesianGrid {...GRID_PROPS} />
              {/* O eixo de datas aparece uma vez só, embaixo, para os dois painéis. */}
              <XAxis
                className="text-ctx-content-placeholder"
                dataKey="date"
                tickFormatter={formatAxisDate}
                stroke="currentColor"
                tick={{ fill: 'currentColor', fontSize: 11 }}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis {...Y_AXIS_PROPS} />
              <Tooltip content={renderChartTooltip(t('usage.charts.messagesSeriesLabel'))} />
              <Bar dataKey="messages" fill="currentColor" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </SurfaceCard>
  );
}
